import React from "react";
import { useI18n, NLink } from "@/i18n/I18nProvider";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1591123120675-6f7f1aae0e5b?w=1600&q=80";

const ICONS = {
  clubs: (
    <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="#C4A46A" strokeWidth="1.5">
      <circle cx="24" cy="24" r="20" />
      <path d="M24 4 a20 12 0 0 0 0 40 M24 4 a20 12 0 0 1 0 40 M4 24 h40 M24 4 v40" />
    </svg>
  ),
  schools: (
    <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="#C4A46A" strokeWidth="1.5">
      <path d="M2 20 L24 8 L46 20 L24 32 Z" />
      <path d="M10 24 V36 C10 38 16 40 24 40 C32 40 38 38 38 36 V24" />
      <path d="M42 22 V32" />
    </svg>
  ),
  institutional: (
    <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="#C4A46A" strokeWidth="1.5">
      <path d="M4 18 L24 6 L44 18" />
      <path d="M8 18 V40 H40 V18" />
      <path d="M14 40 V24 M22 40 V24 M30 40 V24 M38 40 V24" />
      <path d="M4 44 H44" />
    </svg>
  ),
};

const Partners = () => {
  const { t } = useI18n();
  const p = t.partners;

  return (
    <>
      <section className="bg-[#0F1B2D] text-white pt-28 sm:pt-36 pb-16 sm:pb-24 relative grain">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `linear-gradient(rgba(15,27,45,0.85), rgba(15,27,45,0.95)), url(${HERO})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#C4A46A]">{p.kicker}</div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {p.title}
          </h1>
          <span className="gold-rule mt-8 block" />
        </div>
      </section>

      <section className="bg-[#F4F0E8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="border-l border-[#C4A46A]/50 pl-8 lg:sticky lg:top-32">
              <img src="/nc-logo.png" alt="Northcrest" width={64} height={64} className="opacity-90" />
              <p className="mt-10 font-display text-[#0F1B2D] text-2xl lg:text-3xl leading-snug tracking-tight">
                {t.common.tagline.split(". ").map((line, i, arr) => (
                  <span key={line} className="block">
                    {line}{i < arr.length - 1 ? "." : ""}
                  </span>
                ))}
              </p>
              <span className="gold-rule mt-8 block" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[#5E6470] text-lg leading-relaxed">{p.intro}</p>
          </div>
        </div>
      </section>

      <section className="bg-[#F4F0E8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#0F1B2D]/10">
            {p.sections.map((s, i) => (
              <article
                key={s.slug}
                data-testid={`partner-section-${s.slug}`}
                className="bg-[#F4F0E8] p-10 flex flex-col min-h-[360px]"
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-[#C4A46A] text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  {ICONS[s.slug]}
                </div>
                <h2 className="mt-6 font-display text-2xl lg:text-3xl text-[#0F1B2D] tracking-tight">
                  {s.t}
                </h2>
                <span className="gold-rule mt-4 block" />
                <p className="mt-5 text-[#5E6470] text-[15px] leading-relaxed">
                  {s.d}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* NAMED PARTNERS */}
      {p.namedPartners && (
        <section className="bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
            <div className="overline text-[#C4A46A]">{p.namedPartners.kicker}</div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl text-[#0F1B2D] tracking-tight">
              {p.namedPartners.title}
            </h2>
            <span className="gold-rule mt-6 block" />

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0F1B2D]/10">
              {p.namedPartners.items.map((np, i) => (
                <article
                  key={np.name}
                  data-testid={`named-partner-${i}`}
                  className="bg-white p-10 lg:p-14 flex flex-col min-h-[280px]"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="font-display text-[#C4A46A] text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {np.logo && (
                      <a href={np.href} target="_blank" rel="noopener noreferrer" data-testid={`named-partner-logo-link-${i}`}>
                        <img
                          src={np.logo}
                          alt={np.name}
                          className="max-h-20 lg:max-h-24 max-w-[260px] lg:max-w-[300px] w-auto object-contain"
                          loading="lazy"
                        />
                      </a>
                    )}
                  </div>
                  {np.role && (
                    <div className="mt-6 overline text-[#C4A46A]" data-testid={`named-partner-role-${i}`}>{np.role}</div>
                  )}
                  <h3 className={`${np.role ? "mt-3" : "mt-6"} font-display text-3xl lg:text-4xl text-[#0F1B2D] tracking-tight leading-[1.1]`}>
                    {np.name}
                  </h3>
                  <span className="gold-rule mt-6 block" />
                  <p className="mt-6 text-[#5E6470] text-[15px] leading-relaxed">
                    {np.d}
                  </p>
                  {np.href && (
                    <a
                      href={np.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`named-partner-website-${i}`}
                      className="mt-auto pt-8 inline-flex items-center gap-3 text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold group self-start"
                    >
                      <span className="border-b border-[#C4A46A] pb-1 group-hover:text-[#C4A46A] transition-colors">
                        {p.namedPartners.visitCta}
                      </span>
                      <ArrowUpRight size={14} className="text-[#C4A46A]" />
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#0F1B2D] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 sm:py-24 text-center">
          <p className="mx-auto max-w-3xl font-display text-2xl lg:text-3xl leading-snug tracking-tight">
            {p.closing}
          </p>
          <span className="gold-rule mt-10 mx-auto block" />
          <NLink
            to="/contact"
            data-testid="partners-cta"
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#C4A46A] text-[#0F1B2D] text-[12px] tracking-[0.24em] uppercase font-bold hover:bg-[#A38755] transition-colors"
          >
            {t.nav.contact} <ArrowRight size={16} />
          </NLink>
        </div>
      </section>
    </>
  );
};

export default Partners;
